// apps/web/src/components/ErrorBoundary.tsx
// WHY: Catches unhandled React render errors and displays them instead of
// blanking the entire screen. Shows the error message and a reload button.

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Alert, Button, Code, Stack, Text } from '@mantine/core';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // WHY: Log to console so the error is visible in browser dev tools.
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Stack p="xl" gap="md">
          <Alert color="red" title="Something went wrong">
            <Stack gap="sm">
              <Text size="sm">
                An unexpected error occurred. This is likely a bug — please report it.
              </Text>
              <Code block>
                {this.state.error?.message ?? 'Unknown error'}
                {this.state.error?.stack ? `\n\n${this.state.error.stack.slice(0, 500)}` : ''}
              </Code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </Stack>
          </Alert>
        </Stack>
      );
    }

    return this.props.children;
  }
}
